
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { STORAGE_KEYS } from "../../utils/constants";
import { redirectToLogin } from "../../utils/apiClient";
import useScrollPosition from "../../hooks/useScrollPosition";
import { motion } from "framer-motion";
import { MapPin, Calendar, Clock, CheckCircle, Loader2 } from "lucide-react";

import CheckoutHeader from "../../components/servicing/checkout/CheckoutHeader";
import ProgressBar from "../../components/servicing/checkout/ProgressBar";
import AccountSection from "../../components/servicing/checkout/AccountSection";
import OrderSummary from "../../components/servicing/checkout/OrderSummary";
import OrderConfirmationModal from "../../components/servicing/checkout/OrderConfirmationModal";
import LocationSection from "../../components/servicing/checkout/LocationSection";

const API_BASE_URL = (import.meta.env.VITE_BASE_URL || "http://localhost:8080").replace(/\/+$/, "");

const TIME_SLOTS = [
  "9:00 AM - 11:00 AM",
  "11:00 AM - 1:00 PM",
  "1:00 PM - 3:00 PM",
  "3:00 PM - 5:00 PM",
  "5:00 PM - 7:00 PM",
];

const DEFAULT_BOOKED = new Set(["1:00 PM - 3:00 PM"]);

const safeJson = async (resp) => {
  const text = await resp.text();
  try {
    return { data: JSON.parse(text), raw: text };
  } catch {
    return { data: null, raw: text };
  }
};

const getUserIdFromStorageUser = (currentUser) =>
  currentUser?.id ||
  currentUser?.user_id ||
  currentUser?.userid ||
  currentUser?.userId ||
  null;

const inputCls =
  "w-full px-3 py-3 bg-white/[0.04] border border-white/[0.10] text-white rounded-xl focus:border-[#FF6B35]/50 focus:outline-none focus:ring-1 focus:ring-[#FF6B35]/20 transition-all text-sm placeholder-gray-600";

const labelCls = "block text-sm font-semibold text-gray-400 mb-1.5";

const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const scrollPosition = useScrollPosition();

  const step2Ref = useRef(null);
  const step3Ref = useRef(null);
  const step4Ref = useRef(null);

  const [step, setStep] = useState(1);
  const [authChecked, setAuthChecked] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const [serviceMode, setServiceMode] = useState("center");
  const isDoorstep = serviceMode === "doorstep";

  const [cartItems, setCartItems] = useState([]);
  const [bikeData, setBikeData] = useState({
    brand: "Unknown",
    model: "Unknown",
    fuelType: "",
    image: "",
    brandId: null,
    modelId: null,
  });

  const [fuelType, setFuelType] = useState("");

  const [userLocation, setUserLocation] = useState({ lat: null, lng: null });
  const [address, setAddress] = useState({
    flatNo: "",
    area: "",
    landmark: "",
    city: "",
    pincode: "",
    fullAddress: "",
  });
  const [locationError, setLocationError] = useState("");

  const [vehicleNumber, setVehicleNumber] = useState("");
  const [odometerKm, setOdometerKm] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");

  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState("");
  const [bookedSlots] = useState(DEFAULT_BOOKED);

  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);

  const subtotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + (Number(item.discountedPrice) || 0), 0),
    [cartItems]
  );
  const doorstepCharge = 100;
  const gst = useMemo(() => subtotal * 0.05, [subtotal]);
  const total = useMemo(
    () => subtotal + gst + (serviceMode === "doorstep" ? doorstepCharge : 0),
    [subtotal, gst, serviceMode]
  );

  const todayStr = useMemo(() => new Date().toISOString().split("T")[0], []);
  const maxStr = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split("T")[0];
  }, []);

  // AUTH
  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const userDataString = localStorage.getItem(STORAGE_KEYS.USER);
    if (token && userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        setIsLoggedIn(true);
        setCurrentUser(userData);
      } catch {
        setIsLoggedIn(false);
        setCurrentUser(null);
      }
    } else {
      setIsLoggedIn(false);
      setCurrentUser(null);
    }
    setAuthChecked(true);
  }, []);

  // LOAD STATE
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!location.state?.cartItems || !location.state?.bikeData) {
      alert("No items in cart. Redirecting to services page.");
      navigate("/service-packages");
      return;
    }
    setCartItems(location.state.cartItems || []);
    const incomingBike = location.state.bikeData || {};
    setBikeData(incomingBike);
    if (incomingBike?.fuelType && !fuelType) setFuelType(incomingBike.fuelType);
    if (!selectedDate) setSelectedDate(todayStr);
  }, [location.state, navigate, todayStr]); // eslint-disable-line react-hooks/exhaustive-deps

  // VALIDATION
  const step1Valid = useMemo(() => {
    const scheduleOk = !!selectedDate && !!selectedTimeSlot;
    const fuelOk = !!fuelType;
    const locationOk = !!(userLocation?.lat && userLocation?.lng);
    const odoOk =
      odometerKm.trim().length === 0 ||
      (!isNaN(Number(odometerKm)) && Number(odometerKm) >= 0);

    if (!scheduleOk || !fuelOk || !odoOk || !locationOk) return false;

    if (isDoorstep) {
      return (
        address.flatNo.trim().length > 0 &&
        address.area.trim().length > 0 &&
        address.city.trim().length > 0 &&
        address.pincode.trim().length === 6 &&
        address.fullAddress.trim().length > 0
      );
    }
    return true;
  }, [selectedDate, selectedTimeSlot, fuelType, odometerKm, isDoorstep, address, userLocation]);

  const handleRemoveItem = (itemId) => {
    const updatedCart = cartItems.filter((item) => item.id !== itemId);
    setCartItems(updatedCart);
    if (updatedCart.length === 0) {
      alert("Cart is empty. Redirecting to services page.");
      navigate("/");
    }
  };

  const handleContinueFromStep1 = () => {
    if (!authChecked) return;
    if (!isLoggedIn) {
      redirectToLogin("Please login to continue with checkout");
      return;
    }
    if (!step1Valid) {
      alert("Please complete all required fields.");
      return;
    }
    setStep(2);
    setTimeout(() => step2Ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const handleContinueFromStep2 = () => {
    setStep(3);
    setTimeout(() => step3Ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const handleContinueFromStep3 = () => {
    setStep(4);
    setTimeout(() => step4Ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const handleCreateCashBooking = async () => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) { redirectToLogin("Please login to continue"); return; }

    const userId = getUserIdFromStorageUser(currentUser);
    if (!userId) { alert("User info missing. Please login again."); return; }
    if (!cartItems.length) { alert("Your cart is empty."); return; }
    if (!selectedDate || !selectedTimeSlot) { alert("Please select date and time slot."); return; }
    if (!fuelType) { alert("Please select fuel type."); return; }

    const servicesPayload = cartItems.map((item) => ({
      service_id: Number(item.id),
      service_name: item.name || "Service",
      quantity: Number(item.quantity || 1),
      price: Number(item.discountedPrice || 0),
    }));

    const payload = {
      user_id: userId,
      bike_brand: bikeData?.brand || bikeData?.manufacturer || "",
      bike_model: bikeData?.model || "",
      fuel_type: fuelType,
      pincode: address.pincode || "",
      service_type: serviceMode === "doorstep" ? "Doorstep" : "Center",
      issue_description: additionalNotes?.trim() || null,
      pickup_drop: serviceMode === "doorstep" ? 1 : 0,
      doorstep_flat_no: isDoorstep ? address.flatNo : null,
      doorstep_area: isDoorstep ? address.area : null,
      doorstep_landmark: isDoorstep ? address.landmark : null,
      doorstep_city: isDoorstep ? address.city : null,
      doorstep_pincode: isDoorstep ? address.pincode : null,
      user_lat: userLocation?.lat || null,
      user_lng: userLocation?.lng || null,
      service_date: selectedDate,
      service_time_slot: selectedTimeSlot,
      vehicle_number: vehicleNumber?.trim() || null,
      kilometer: odometerKm?.trim() ? Number(odometerKm) : null,
      engine_number: null,
      chassis_number: null,
      total_amount: Number(total),
      payment_type: "Cash",
      services: servicesPayload,
    };

    const resp = await fetch(`${API_BASE_URL}/booking/create`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });

    const { data, raw } = await safeJson(resp);
    if (!resp.ok || !data?.success) {
      const msg = data?.message || raw || `Server error ${resp.status}`;
      throw new Error(msg);
    }
    return data;
  };

  const handlePayment = async () => {
    if (!userLocation?.lat || !userLocation?.lng) {
      alert("Please select your location on the map before placing a booking.");
      return;
    }
    if (isDoorstep) {
      if (!address.flatNo.trim()) return alert("Please enter flat / house no.");
      if (!address.area.trim()) return alert("Please enter area / locality.");
      if (!address.city.trim()) return alert("Please enter city.");
      if (address.pincode.trim().length !== 6) return alert("Please enter a valid 6-digit pincode.");
      if (!address.fullAddress.trim()) return alert("Please enter full address.");
    }
    try {
      const result = await handleCreateCashBooking();
      setOrderDetails({
        orderId: result.booking_id || `BK${Date.now().toString().slice(-8)}`,
        cartItems,
        bikeDetails: {
          manufacturer: bikeData.brand || bikeData.manufacturer,
          model: bikeData.model,
          fuelType,
          vehicleNumber,
          kilometer: odometerKm,
          notes: additionalNotes,
        },
        selectedStore: isDoorstep ? "Doorstep Service" : "Service Center",
        selectedDate,
        selectedTimeSlot,
      });
      setShowOrderConfirmation(true);
    } catch (err) {
      console.error("BOOKING FAILED:", err);
      alert(`Failed to place booking: ${err.message}`);
    }
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-[#FF6B35] animate-spin" />
          <p className="text-gray-400 text-sm font-medium">Checking authentication...</p>
        </div>
      </div>
    );
  }

  const scrollProgress = Math.min(
    (scrollPosition / Math.max(document.body.scrollHeight - window.innerHeight, 1)) * 100,
    100
  );

  return (
    <div className="min-h-screen flex flex-col bg-[#0A0A0A]">
      {/* Scroll progress bar */}
      <div className="h-0.5 bg-white/[0.06] fixed top-0 left-0 right-0 z-50">
        <motion.div
          className="h-full bg-[#FF6B35] rounded-full"
          style={{ width: `${scrollProgress}%` }}
          transition={{ duration: 0.1 }}
        />
      </div>

      <CheckoutHeader />

      <div className="flex-grow flex flex-col lg:flex-row gap-6 px-4 sm:px-6 lg:px-8 py-6 mt-1 max-w-7xl mx-auto w-full">
        {/* Main */}
        <main className="flex-grow lg:w-2/3 order-1 space-y-5">
          <ProgressBar currentStep={step} />

          {/* Step 1 — Service Details */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-[#111111] border border-white/[0.08] rounded-2xl p-4 sm:p-6 space-y-6"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-xl flex items-center justify-center">
                <MapPin className="h-5 w-5 text-[#FF6B35]" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-white">1) Service Location</h2>
                <p className="text-xs text-gray-500">Choose how you want service</p>
              </div>
            </div>

            <LocationSection
              serviceMode={serviceMode}
              setServiceMode={setServiceMode}
              userLocation={userLocation}
              setUserLocation={setUserLocation}
              address={address}
              setAddress={setAddress}
              locationError={locationError}
              setLocationError={setLocationError}
            />

            {/* Fuel type */}
            <div>
              <label className={labelCls}>Fuel Type *</label>
              <select
                value={fuelType}
                onChange={(e) => setFuelType(e.target.value)}
                className={inputCls + " bg-[#111111]"}
              >
                <option value="">Select fuel type</option>
                <option value="Petrol">Petrol</option>
                <option value="Diesel">Diesel</option>
                <option value="Electric">Electric</option>
                <option value="CNG">CNG</option>
                <option value="Hybrid">Hybrid</option>
              </select>
              <p className="text-xs text-gray-600 mt-1">This will be saved with booking details.</p>
            </div>

            {/* Vehicle + odometer */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Vehicle Number (Optional)</label>
                <input
                  type="text"
                  value={vehicleNumber}
                  onChange={(e) => setVehicleNumber(e.target.value)}
                  placeholder="e.g., MH12AB1234"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Odometer Reading (km)</label>
                <input
                  type="text"
                  value={odometerKm}
                  onChange={(e) => setOdometerKm(e.target.value.replace(/\D/g, "").slice(0, 7))}
                  placeholder="e.g., 12500"
                  className={inputCls}
                />
              </div>
            </div>

            {/* Schedule */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#FF6B35]" />
                <h3 className="text-sm sm:text-base font-bold text-white">2) Schedule Your Service</h3>
              </div>

              <div>
                <label className={labelCls}>Preferred Date *</label>
                <input
                  type="date"
                  value={selectedDate}
                  min={todayStr}
                  max={maxStr}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className={inputCls + " bg-[#111111]"}
                />
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-400 mb-2 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-[#FF6B35]" />
                  Select Time Slot *
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {TIME_SLOTS.map((slot) => {
                    const isBooked = bookedSlots.has(slot);
                    const isSelected = selectedTimeSlot === slot;
                    return (
                      <button
                        key={slot}
                        onClick={() => !isBooked && setSelectedTimeSlot(slot)}
                        disabled={isBooked}
                        className={[
                          "px-4 py-3 rounded-xl border text-sm font-semibold transition-all flex items-center justify-between",
                          isBooked
                            ? "border-white/[0.04] bg-white/[0.02] text-gray-700 cursor-not-allowed"
                            : isSelected
                            ? "border-[#FF6B35] bg-[#FF6B35]/10 text-[#FF6B35] shadow-lg shadow-[#FF6B35]/10"
                            : "border-white/[0.08] bg-white/[0.03] text-gray-300 hover:border-[#FF6B35]/30 hover:text-white",
                        ].join(" ")}
                      >
                        <span>{slot}</span>
                        {isBooked ? (
                          <span className="text-xs font-bold text-gray-700">Booked</span>
                        ) : isSelected ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className={labelCls}>Additional Notes (Optional)</label>
                <textarea
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e.target.value)}
                  placeholder="Any issues, pickup instructions, etc."
                  rows={3}
                  className={inputCls + " resize-none"}
                />
              </div>
            </div>
          </motion.div>

          {/* Step 2 — Account */}
          <AccountSection
            ref={step2Ref}
            step={step}
            isLoggedIn={isLoggedIn}
            currentUser={currentUser}
            redirectToLogin={redirectToLogin}
          />

          {/* Step 3 — Review */}
          <div ref={step3Ref} className="bg-[#111111] border border-white/[0.08] rounded-2xl p-4 sm:p-6 space-y-4">
            <h2 className="text-base sm:text-lg font-bold text-white">3) Review</h2>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between py-2 border-b border-white/[0.06]">
                <span className="text-gray-500">Service Type</span>
                <span className="font-semibold text-white">{isDoorstep ? "Doorstep Service" : "Service Center"}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-white/[0.06]">
                <span className="text-gray-500">Fuel Type</span>
                <span className="font-semibold text-white">{fuelType}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-white/[0.06]">
                <span className="text-gray-500">Schedule</span>
                <span className="font-semibold text-white">{selectedDate} / {selectedTimeSlot}</span>
              </div>
              {isDoorstep && (
                <div className="flex justify-between py-2">
                  <span className="text-gray-500">Address</span>
                  <span className="font-semibold text-white text-right max-w-[60%]">
                    {[address.flatNo, address.area, address.landmark, address.city, address.pincode]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </div>
              )}
            </div>

            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={handleContinueFromStep3}
              className="w-full px-6 py-4 bg-[#FF6B35] hover:bg-[#E85520] text-white font-bold rounded-xl transition-colors shadow-lg shadow-[#FF6B35]/20"
            >
              Continue to Payment
            </motion.button>
          </div>

          {/* Step 4 — Payment */}
          <div ref={step4Ref} className="bg-[#111111] border border-white/[0.08] rounded-2xl p-4 sm:p-6 space-y-4">
            <h2 className="text-base sm:text-lg font-bold text-white">4) Payment</h2>

            <div className="p-5 bg-green-500/10 border border-green-500/20 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-bold text-white mb-1">Cash on Delivery</h3>
                  <p className="text-sm text-gray-400 mb-3">
                    Pay with cash when your service is completed. This is the only payment method available currently.
                  </p>
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm font-semibold">Selected</span>
                  </div>
                </div>
              </div>
            </div>

            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={handlePayment}
              className="w-full px-6 py-4 bg-[#FF6B35] hover:bg-[#E85520] text-white font-black text-base rounded-xl transition-colors shadow-lg shadow-[#FF6B35]/20 flex items-center justify-center gap-2"
            >
              <CheckCircle className="h-5 w-5" />
              Place Booking
            </motion.button>
          </div>
        </main>

        {/* Sidebar */}
        <aside className="lg:w-1/3 order-2">
          <OrderSummary
            cartItems={cartItems}
            bikeData={{
              manufacturer: bikeData.brand || bikeData.manufacturer,
              model: bikeData.model,
              fuelType: fuelType || bikeData.fuelType || "",
              image: bikeData.image,
            }}
            subtotal={subtotal}
            gst={gst}
            doorstepCharge={serviceMode === "doorstep" ? doorstepCharge : 0}
            total={total}
            handleRemoveItem={handleRemoveItem}
            currentStep={step}
            isLoggedIn={isLoggedIn}
            step1Valid={step1Valid}
            handleContinueStep1={handleContinueFromStep1}
            handleContinueStep2={handleContinueFromStep2}
          />

          {step === 3 && (
            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={handleContinueFromStep3}
              className="w-full mt-4 px-6 py-4 bg-[#FF6B35] hover:bg-[#E85520] text-white font-bold rounded-xl transition-colors shadow-lg shadow-[#FF6B35]/20"
            >
              Continue to Payment
            </motion.button>
          )}
        </aside>
      </div>

      <OrderConfirmationModal
        show={showOrderConfirmation}
        orderDetails={orderDetails}
        onOk={() => { setShowOrderConfirmation(false); navigate("/service-history"); }}
        onClose={() => { setShowOrderConfirmation(false); navigate("/service-history"); }}
      />
    </div>
  );
};

export default CheckoutPage;
